import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import ContentLayout from "../../components/layout-components/ContentLayout";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/css";
import GenerateSummary from "./GenerateSummary";
import AiIcon from "../../assets/images/ralvie/ai_icon_lg.svg";
import LavoroAiIcon from "../../assets/images/lavoro-ai.svg";
import LavoroAiDarkIcon from "../../assets/images/lavoro-ai-dark.svg";
import CommonUtil from "../../util/CommonUtils";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AiContext, sidebarContext } from "../../util/context";
import Typewriter from "../../components/Typewriter";
import CustomButton from "../../components/CustomButton";
import { useLocation, useNavigate } from "react-router-dom";
import CustomIconButton from "../../components/CustomIconButton";
import { ReactComponent as HistoryIcon } from "../../assets/images/ralvie/icon-history.svg";
import { ReactComponent as DarkCopyIcon } from "../../assets/images/Darkcopy.svg";
import { ReactComponent as LightCopyIcon } from "../../assets/images/copy.svg";
import { ReactComponent as LightCancelIcon } from "../../assets/images/cancel.svg";
import { ReactComponent as DarkCancelIcon } from "../../assets/images/closeIcon.svg";
import { ReactComponent as CopyIconHover } from "../../assets/images/copy_hover.svg";
import { ReactComponent as DarkCopyIconHover } from "../../assets/images/DarkCopyIconHover.svg";
import { ReactComponent as CancelIconHover } from "../../assets/images/cancel_hover.svg";
import AppContext from "../../config/AppContext";
import { ReactComponent as RefreshIcon } from "../../assets/images/refresh_icon.svg";
import { toast } from "sonner";
import dayjs from "dayjs";
import { getSubscriptionLimit } from "../../services/Subscription";
import UpgradePopup from "../../components/subscription-component/UpgradePopup";
import RestrictPermissionPopup from "../../components/subscription-component/RestrictPermissionPopup";

export default function AiSummary() {
  const { t } = useTranslation();
  const [generatedSummary, setGenerateSummary] = useState(undefined);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isXlScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const [isHovered, setIsHovered] = useState(false);
  const [isCancelHovered, setCancelIsHovered] = useState(false);
  const { mode } = useContext(AppContext);
  const [isRefreshClicked, setIsRefreshClicked] = useState(0);
  const [upgradePopupOpen, setUpgradePopupOpen] = useState(false);
  const [restrictPopupOpen, setRestrictPopupOpen] = useState(false);
  const { subscriptionDetails } = useContext(sidebarContext);

  const location = useLocation();
  const { state } = location || {};

  const summaryRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isTypewriterActive, setIsTypewriterActive] = useState(false);

  // Debounce scroll event
  useEffect(() => {
    const handleScroll = () => {
      setIsUserScrolling(true);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => setIsUserScrolling(false), 1000);
    };

    let scrollTimeout;
    const summaryElement = summaryRef.current;
    summaryElement?.addEventListener("scroll", handleScroll);

    return () => {
      summaryElement?.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Smooth scroll when generatedSummary changes
  useEffect(() => {
    if (!isUserScrolling && !isTypewriterActive) {
      const container = summaryRef.current;
      if (container) {
        requestAnimationFrame(() => {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth",
          });
        });
      }
    }
  }, [generatedSummary, isUserScrolling, isTypewriterActive]);

  const processedSummary = useMemo(() => {
    return generatedSummary?.replaceAll("**", "");
  }, [generatedSummary]);

  const handleTypewriterStart = () => {
    setIsTypewriterActive(true);
  };

  const handleTypewriterEnd = () => {
    setIsTypewriterActive(false);
    requestAnimationFrame(() => {
      summaryRef.current?.scrollTo({
        top: summaryRef.current.scrollHeight,
        behavior: "smooth",
      });
    });
  };

  useEffect(() => {
    getSubscriptionLimit("DAILY_AI_SUMMARIZATION_LIMITS").then((res) => {
      if (res.data.code === "RCI0000") {
        console.log(res.data.data);
        const { currValue, limit } = res.data.data;
        if (currValue >= limit) {
          if (!["OWNER"].includes(CommonUtil.getRoleCode())) {
            setRestrictPopupOpen(true);
            return;
          } else {
            setUpgradePopupOpen(true);
            return;
          }
        }
      } else if (res?.data?.code === "RCE0225") {
          if (!["OWNER"].includes(CommonUtil.getRoleCode())) {
                setRestrictPopupOpen(true);
          } else {
                setUpgradePopupOpen(true);
        }
      }
      else {
        navigate("/dashboard");
      }
    });
  }, []);

  const getPopupTitle = (subscriptionDetails, t, isRestrictPopup = false) => {
    if (CommonUtil.getStatusCondition(subscriptionDetails)) {
      return isRestrictPopup ? t("RAL0902") : t("RAL0867");
    } else if (CommonUtil.isPlanCodeDefault(subscriptionDetails)) {
      return t("RAL0850");
    } else {
      return t("RAL0852");
    }
  };
  
  const getPopupContent = (subscriptionDetails, t, isRestrictPopup = false) => {
    if (CommonUtil.getStatusCondition(subscriptionDetails)) {
      return isRestrictPopup ? t("RAL0903") : t("RAL0868");
    } else if (CommonUtil.isPlanCodeDefault(subscriptionDetails)) {
      return t("RAL0849");
    } else {
      return t("RAL0851");
    }
  };

  const actions = (
    <Box sx={{ display: "flex" }}>

      <Box mr={2.2}>
        <Box
          sx={{
            "& .refresh_svg": {
              transition: "color 0.3s ease",
              color: mode === "dark" ? "var(--color-33-50)" : "#242424cc",
            },
            "&:hover": {
              "& .hover-text, & .refresh_svg": {
                color: mode === "dark" ? "#f8f8f8cc" : "#242424",
              },
            },
          }}
        >
          <CustomIconButton
            id="btn-refresh-tags"
            title={t("RAL0169")}
            size={isXlScreen ? "large" : "md"}
            variant="square"
            lightMode={
              <RefreshIcon
                className="refresh_svg"
                height={isXlScreen ? "18px" : "14px"}
              />
            }
            darkMode={
              <RefreshIcon
                className="refresh_svg"
                height={isXlScreen ? "18px" : "14px"}
              />
            }
            onClick={() => setIsRefreshClicked(isRefreshClicked + 1)}
          />
        </Box>
      </Box>

      <Box>
        <CustomIconButton
          type="contained"
          id="btn-custom-member"
          name="btn-custom-member"
          title={t("RAL0381")}
          size={isXlScreen ? "large" : "md"}
          variant="square"
          lightMode={<HistoryIcon height={isXlScreen ? "18px" : "14px"} />}
          darkMode={<HistoryIcon height={isXlScreen ? "18px" : "14px"} />}
          onClick={() => navigate("/ai/summary/history")}
        />
      </Box>
    </Box>
  );
  const handleCancelSummary = () => {
    setGenerateSummary(undefined);
    setIsGeneratingSummary(false);
    setIsHovered(false);
    setCancelIsHovered(false);
  };

  return (
    <>
      <AiContext.Provider value={{ generatedSummary, setGenerateSummary }}>
        <ContentLayout title={t("RAL0345")} navBack={true} actions={actions}>
          <div
            className={css`
              display: flex;
              column-gap: 2px;
              width: 100%;
            `}
          >
            {generatedSummary && (
              <div
                className={css`
                  background: ${mode === "dark" ? "#101010" : "#ffffff"};
                  border-radius: 5px;
                  opacity: 1;
                  width: 70%;
                  height: 85vh !important;
                  overflow-y: auto;
                  padding: 16px !important;
                  position :relative;
                  scroll-behavior: smooth;
                `}
              >
                <Box
                  display={"flex"}
                  flexDirection={"row-reverse"}
                  minWidth={"100%"}
                  columnGap={1}
                >
                  <Box
                    item
                    onMouseLeave={() => {
                      if (!isGeneratingSummary) {
                        setIsHovered(false);
                      }
                    }}
                    onMouseOver={() => {
                      if (!isGeneratingSummary) {
                        setIsHovered(true);
                      }
                    }}
                  >
                    {isHovered ? (
                      <CustomButton
                        class={mode === "dark" ? "custom-button-copy-Dark-Hover" : "custom-button-copy-Light-Hover"}
                        variant="contained"
                        onClick={() => {
                          const summaryDiv = document.querySelector(".summary");
                          const generatedSummary =
                            summaryDiv.innerText || summaryDiv.textContent;
                          CommonUtil.copyToClipboard(generatedSummary);
                          toast.success(t("RAL0684"));
                        }}
                      >
                        {mode === "dark" ? (
                          <CopyIconHover
                            style={{
                              width: "18px",
                              height: "18px",
                              // marginRight: "10px",
                            }}
                          />
                        ) : (
                          <DarkCopyIconHover
                            style={{
                              width: "18px",
                              height: "18px",

                            }}
                          />
                        )}
                        {/* {t("RAL0569")} */}
                      </CustomButton>
                    ) : (
                      <CustomButton
                        class={`custom-button-copy ${mode === "dark" ? "dark" : "light"
                          }`}
                        onClick={() => {
                          const summaryDiv = document.querySelector(".summary");
                          const generatedSummary =
                            summaryDiv.innerText || summaryDiv.textContent;
                          CommonUtil.copyToClipboard(generatedSummary);
                          toast.success(t("RAL0684"));
                        }}
                      >
                        {mode === "dark" ? (
                          <DarkCopyIcon
                            style={{
                              width: "18px",
                              height: "18px",
                              // marginRight: "10px",
                            }}
                          />
                        ) : (
                          <LightCopyIcon
                            style={{
                              width: "18px",
                              height: "18px",
                              // marginRight: "10px",  
                            }}
                          />
                        )}
                        {/* {t("RAL0569")} */}
                      </CustomButton>
                    )}
                  </Box>
                  <Box
                    item
                    style={{
                      height: "3.125rem",
                      paddingRight: "0.5rem"
                    }}
                    onMouseLeave={() => {
                      if (!isGeneratingSummary) {
                        setCancelIsHovered(false);
                      }
                    }}
                    onMouseOver={() => {
                      if (!isGeneratingSummary) {
                        setCancelIsHovered(true);
                      }
                    }}
                  >
                    {isCancelHovered ? (
                      <CustomButton
                        class="custom-button-cancelHover"
                        onClick={handleCancelSummary}
                      >
                        <CancelIconHover
                          style={{
                            width: "12px",
                            height: "12px",
                            marginRight: "10px",
                          }}
                        />
                        {t("RAL0177")}
                      </CustomButton>
                    ) : (
                      <CustomButton
                        class={`custom-button-cancel ${mode === "dark" ? "dark" : "light"
                          }`}
                        onClick={handleCancelSummary}
                      >
                        {mode === "dark" ? (
                          <DarkCancelIcon
                            style={{
                              width: "12px",
                              height: "12px",
                              marginRight: "10px",
                            }}
                          />
                        ) : (
                          <LightCancelIcon
                            style={{
                              width: "12px",
                              height: "12px",
                              marginRight: "10px",
                            }}
                          />
                        )}
                        {t("RAL0177")}
                      </CustomButton>
                    )}
                  </Box>
                </Box>
                <div
                  ref={summaryRef}
                  className={css`
                    height: 92% !important;
                    overflow-y: auto;
                    scroll-behavior: smooth;
                  `}
                >
                  <Typewriter
                    text={processedSummary}
                    onStart={handleTypewriterStart}
                    onEnd={handleTypewriterEnd}
                    delay={10}
                  />
                </div>
              </div>
            )}
            {!generatedSummary && (
              <div
                className={css`
                  background: ${mode === "dark" ? "#101010" : "#ffffff"};
                  border-radius: 5px;
                  opacity: 1;
                  width: 70%;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  row-gap: 16px;
                  height: 85vh !important;
                  overflow-y: auto;
                `}
              >
                {/* <img src={AiIcon} alt="AI" height={"260"} width={"210"} />
                <Typography
                  variant="header2"
                  sx={{
                    color: "var( --text-secondary-80)",
                    font: "normal normal bold 18px/20px Gotham",
                    letterSpacing: "0px",
                  }}
                >
                  {t("RAL0039") + " " + CommonUtil.getLoggedUser()?.firstName}
                </Typography> */}

                {mode === "dark" ? <img src={LavoroAiDarkIcon} alt="AI" /> :
                  <img src={LavoroAiIcon} alt="AI" />}

                <Typography
                  sx={{
                    color: "var(--text-secondary-80)",
                    font: "normal normal normal 33px/49px GothamMedium",
                    fontWeight: 600,
                    letterSpacing: "0px",
                    background:
                      "-webkit-linear-gradient(20deg, #442EB2 0%, #D8583D 51%, #FA9C2B 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {t("RAL0386")}
                </Typography>
                <Typography
                  sx={{
                    color: "var( --text-secondary-80)",
                    font: "normal normal normal 16px/28px Gotham;",
                    letterSpacing: "0px",
                    padding: CommonUtil.getOS() === "MacOS" ? "10px" : "0px",
                  }}
                >
                  {t("RAL0355")}
                </Typography>
              </div>
            )}
            <div
              className={css`
                background: ${mode === "dark" ? "#101010" : "#ffffff"};
                border-radius: 5px;
                opacity: 1;
                width: 30%;
                padding: 16px;
                height: 85vh !important;
                overflow-y: auto;
                margin-bottom: 10px;
              `}
            >
              <GenerateSummary logdate={state?.date} key={isRefreshClicked} />
            </div>
          </div>

          {["OWNER"].includes(CommonUtil.getRoleCode()) && (
          <UpgradePopup
            open={upgradePopupOpen}
            onClose={() => setUpgradePopupOpen(false)}
            onConfirm={() => {
              if (CommonUtil.getRoleCode() === "OWNER") {
                navigate("/subscription-manage");
              } else {
                navigate("/page-not-found");
              }
            }}
            title={getPopupTitle(subscriptionDetails, t)}
            content={getPopupContent(subscriptionDetails, t)}
            cancelText={t("RAL0177")}
            confirmText={t("RAL0848")}
          />
        )} 

     {!["OWNER"].includes(CommonUtil.getRoleCode()) && ( 
          <RestrictPermissionPopup
            open={restrictPopupOpen}
            onClose={() => setRestrictPopupOpen(false)}
            title={getPopupTitle(subscriptionDetails, t, true)}
            content={getPopupContent(subscriptionDetails, t, true)}
            cancelText={t("RAL0177")}
          />
        )}

        </ContentLayout>
      </AiContext.Provider>
    </>
  );
}
